import { useEffect, useRef, useState } from 'react';

/**
 * Screen Wake Lock for the duration of an active workout. Master Phase 6.
 *
 * `PHASE-0-ARCHITECTURE.md` §14: "Wake Lock holds the screen on for the
 * whole workout and is released the moment it ends." Held while `active` is
 * true, released on `active` going false or on unmount.
 *
 * **Why `visibilitychange` matters here specifically:** the Wake Lock API
 * releases itself automatically whenever the tab is hidden (backgrounded,
 * screen locked) — it does not silently keep trying in the background, and
 * it does **not** re-acquire itself when the tab becomes visible again. A
 * workout that gets backgrounded even briefly (switching to check a timer
 * app, a phone call) would otherwise permanently lose the wake lock for the
 * rest of the session unless this re-acquires it on return.
 */
export function useWakeLock(active: boolean): { supported: boolean; held: boolean } {
  const [held, setHeld] = useState(false);
  const lockRef = useRef<WakeLockSentinel | null>(null);
  const supported = typeof navigator !== 'undefined' && 'wakeLock' in navigator;

  useEffect(() => {
    if (!supported || !active) return;

    let cancelled = false;

    async function acquire(): Promise<void> {
      try {
        const lock = await navigator.wakeLock.request('screen');
        if (cancelled) {
          // The effect was torn down while the request was in flight.
          await lock.release();
          return;
        }
        lockRef.current = lock;
        setHeld(true);
        lock.addEventListener('release', () => setHeld(false));
      } catch {
        // A denied or unavailable lock is not fatal to the workout — the
        // screen just won't be held on. Nothing here retries indefinitely.
        setHeld(false);
      }
    }

    function onVisibilityChange(): void {
      if (document.visibilityState === 'visible' && lockRef.current === null) {
        void acquire();
      }
    }

    void acquire();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisibilityChange);
      const lock = lockRef.current;
      lockRef.current = null;
      setHeld(false);
      if (lock !== null) void lock.release();
    };
  }, [active, supported]);

  return { supported, held };
}
